'use client';
import { getHotWords, getSuggestWords } from '@/apis/s/s.api';
import { ShopInfoVO, TSearchEventTracks } from '@/apis/s/s.interface';
import { buildSearchUrl, lockPage, resetLockPage } from '@/app/s/util';
import { spmTrackingUtils } from '@/intl-jdi-common-ssr/utils/spmTrackUtils';
import ArrowLeftIcon from '@/assets/icons/ArrowLeft.svg';
import ClearBtn from '@/assets/icons/Clear.svg';
import DeleteIcon from '@/assets/icons/Delete.svg';
import SearchBtn from '@/assets/icons/searchBtn.svg';
import ShopRight from '@/assets/icons/shop-right.svg';
import SearchRight from '@/assets/icons/search-right.svg';
import CDNImage from '@/components/cdnImage/cdnImage';
import UKDialog from '@/components/UKDialog';
import UKToast from '@/components/UKToast';
import { useTranslation } from '@/i18n/client';
import { useStores } from '@/models';
import {
  EXPOSURE_RECORD as EXPOSURE_RECORD24270,
  sendClickTrack as sendClickTrack24270,
} from '@/tracks/24270';
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/24966';
import {
  EXPOSURE_RECORD as EXPOSURE_RECORD24967,
  sendClickTrack as sendClickTrack24967,
  sendPv as sendPv24967,
} from '@/tracks/24967';
import { EXPOSURE_RECORD as EXPOSURE_RECORD24968 } from '@/tracks/32525';
import { EXPOSURE_RECORD as EXPOSURE_RECORD25103 } from '@/tracks/25103';
import { EXPOSURE_RECORD as EXPOSURE_RECORD32535 } from '@/tracks/32535';
import { debounce } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useRouter, useSearchParams } from 'next/navigation';
import { MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';
import {
  SEARCH_EVENT_NAMES,
  searchBusinessLogger,
} from '@/utils/logger/instance/searchBusinessLogger';

interface SearchBarProps {
  id?: string;
  onInputClick?: MouseEventHandler<HTMLInputElement>;
  afterSearchBtnClick?: (keyword: string, event: any) => void;
  searchGoBack?: () => void;
  logid: string;
  k: string;
  autoFocus?: boolean;
  baseUrl?: string;
  isAddon?: boolean;
  shopId?: string;
  shopInfoVO: ShopInfoVO;
  shopKeywords?: string;
  searchEventTrack: TSearchEventTracks;
  containerClass?: string;
  rankingSearch?: boolean;
  disableAutoShowDropdown?: boolean; // 新增：是否禁用焦点时自动显示下拉框
}

const SearchBar = observer(
  ({
    id,
    onInputClick,
    afterSearchBtnClick,
    searchGoBack,
    logid,
    k,
    autoFocus = true,
    baseUrl,
    isAddon,
    shopId,
    shopInfoVO,
    shopKeywords,
    searchEventTrack,
    containerClass = '',
    rankingSearch,
    disableAutoShowDropdown = false,
  }: SearchBarProps) => {
    const { searchStore } = useStores();
    const { t } = useTranslation();
    const router = useRouter();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [againDelete, setAgainDelete] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const searchParams = useSearchParams();

    // 获取小首页tab名称
    const channelPageName = searchParams.get('channelPageName') || '';
    const poolIdList = searchParams.get('poolIdList') || '';
    //获取点击的底纹词
    const defaultWords = searchParams.get('dw');
    const algoid = searchParams.get('al') || '';
    const requestId = searchParams.get('r') || '';

    //榜单搜索
    const isRankingSearch =
      rankingSearch && searchParams.get('topKeyFilters')?.includes('bestseller');

    useEffect(() => {
      (async () => {
        searchStore.initRecentKeywords();
        const search = new URLSearchParams(window.location.search);
        const frontCategoryId = search.get('l3') || search.get('l2') || search.get('l1');
        // 进入页面聚焦input框
        if (!k && !frontCategoryId && autoFocus && !shopId && !shopKeywords) {
          (inputRef.current as HTMLElement)?.focus();
        } else {
          (inputRef.current as HTMLElement)?.blur();
          searchStore.setSearchDropdownVisible(false);
        }
        await getHotWordList();
      })();
    }, []);

    useEffect(() => {
      searchStore.searchDropdownVisible ? lockPage() : resetLockPage();
      // if (searchStore.searchDropdownVisible) {
      //   // sendPv.Search_Sug();
      // } else if (!k) {
      //   sendPv24967.Search_Activity();
      // }
    }, [searchStore.searchDropdownVisible]);

    useEffect(() => {
      // 后退时清空关键词和联想词
      const onPopState = () => {
        searchStore.setKeywords('');
        searchStore.setSuggestWords([]);
      };
      window.addEventListener('popstate', onPopState);
      return () => window.removeEventListener('popstate', onPopState);
    }, []);
    // 获取地址栏shopId或者storeId，用来判断店内搜索，不展示搜索历史，以及热词传入shopId字段
    const getShopId = () => {
      return searchParams.get('shopId') || searchParams.get('storeId');
    };

    // 获取热词列表
    const getHotWordList = async () => {
      searchStore.setIsLoading(true);
      const hotWordsResult = await getHotWords({ num: 15, shopId: getShopId() || '' });
      if (hotWordsResult.success && hotWordsResult.data) {
        searchStore.setHotWords(hotWordsResult.data.hotWordsItems || []);
      }
      searchStore.setIsLoading(false);
    };

    const handleSearch = (event: any) => {
      setIsInputFocused(false);
      if (!searchStore.keywords.trim() && !searchStore.shopKeywords.trim()) {
        if (defaultWords) {
          searchStore.setKeywords(defaultWords);
          searchStore.setSortType('');
          searchStore.setFrontCategoryId('');
          searchStore.reset();
          searchStore.updateRecentKeywords(defaultWords);
          // searchStore.setSearchDropdownVisible(false);
          searchStore.setFilterDropdownVisible(false);
          if (isRankingSearch) {
            searchStore.setSelectedTopKeys(['bestseller']);
          }
          spmTrackingUtils.spmClick({
            spm: 'a0146.b002227.c00002381.SearchBtn',
            biz_type: '',
            event_id: 'Search_SearchBtn',
            page_id: 'Search_Activity',
            json_param: JSON.stringify({
              abver: '-100',
              pvid: logid || '-100',
              channelName: 'Search_result',
              keyWord: defaultWords,
              shopid: shopId || '-100',
              searchtype: 'hints',
              ...searchEventTrack,
            }),
            target: event.target as HTMLElement,
          });
          router.push(
            buildSearchUrl(
              {
                ...(shopId && { shopId }),
                ...searchStore.searchParams,
                st: 'hints',
                al: algoid,
                r: requestId,
                keywordsEventType: 4,
              },
              baseUrl,
            ),
          );
          afterSearchBtnClick && afterSearchBtnClick(defaultWords, event);
          return;
        }
        UKToast.show({
          content: t('s.searchBar.searchKeywords'),
          icon: 'error',
        });
        afterSearchBtnClick && afterSearchBtnClick(searchStore.keywords, event);
        return;
      }
      spmTrackingUtils.spmClick({
        spm: 'a0146.b002227.c00002381.SearchBtn',
        biz_type: '',
        event_id: 'Search_SearchBtn',
        page_id: 'Search_Activity',
        json_param: JSON.stringify({
          abver: '-100',
          pvid: logid || '-100',
          channelName: 'Search_result',
          keyWord: searchStore.keywords.trim(),
          shopid: shopId || '-100',
          searchtype: 'active search',
          ...searchEventTrack,
        }),
        target: event.target as HTMLElement,
      });
      searchStore.setSortType('');
      searchStore.reset();
      searchStore.setFrontCategoryId('');
      searchStore.updateRecentKeywords(searchStore.keywords);
      searchStore.setShopKeywords(shopId ? searchStore.shopKeywords : '');
      // searchStore.setSearchDropdownVisible(false);
      searchStore.setFilterDropdownVisible(false);
      if (isRankingSearch) {
        searchStore.setSelectedTopKeys(['bestseller']);
      }
      router.push(
        buildSearchUrl(
          {
            ...(shopId && { shopId }),
            ...searchStore.searchParams,
            st: 'active search',
            keywordsEventType: '0',
          },
          baseUrl,
        ),
      );
      afterSearchBtnClick && afterSearchBtnClick(searchStore.keywords, event);
    };

    const handleSelectKeywords = (
      keyword: string,
      miniKeyword: string,
      type?: number,
      storeId?: string,
      searchType?: string,
      hotExtMap?: any,
      keywordsEventType?: string,
    ) => {
      setIsInputFocused(false);
      if (!keyword) {
        return;
      }
      searchStore.setSortType('');
      searchStore.reset();
      searchStore.setFrontCategoryId('');
      searchStore.updateRecentKeywords(keyword);

      searchStore.setShopKeywords(shopId ? keyword : '');
      searchStore.setFilterDropdownVisible(false);

      if (miniKeyword) {
        searchStore.toggleSelectedTopKey('channel_page_filter');
      }

      if (rankingSearch) {
        searchStore.setIsRankingSearch(true);
      }
      if (isRankingSearch) {
        searchStore.setSelectedTopKeys(['bestseller']);
      }

      searchStore.setKeywords(keyword);
      searchStore.setChannelPageName(channelPageName);
      searchStore.setPoolIdList(poolIdList);

      const { channelPage, filterByPoolIds, ...otherSearchParams } = searchStore.searchParams;
      if (type === 1) {
        router.push(`/store/${storeId}`);
      } else {
        router.push(
          buildSearchUrl(
            {
              ...(shopId && { shopId }),
              ...(channelPageName && { channelPageName }),
              ...(poolIdList && { poolIdList }),
              st: searchType,
              al: searchType === 'hot search' ? hotExtMap?.algoid : '-100',
              r: searchType === 'hot search' ? hotExtMap?.requestId : '-100',
              ...otherSearchParams,
              keywordsEventType,
            },
            baseUrl,
          ),
        );
      }
      // 等路由切换后再隐藏热词挡板，否则之前的记录会闪一下
      // setTimeout(() => {
      //   searchStore.setSearchDropdownVisible(false);
      // }, 100);
    };

    const handleClear = () => {
      searchStore.setKeywords('');
      searchStore.setShopKeywords(''); // 清空店铺内搜索词
      searchStore.setSuggestWords([]);
      searchStore.setSearchDropdownVisible(true);
    };

    const handleClearRecentKeywords = () => {
      setAgainDelete(true);
    };

    const deleteRecentKeywords = () => {
      searchStore.clearRecentKeywords();
      setAgainDelete(false);
    };

    // 提取公共函数 - 用来清空关键词
    const handleEmptyKeyword = (searchStore: any) => {
      searchStore.setSuggestWords([]);
      searchStore.setSearchDropdownVisible(true);
    };

    // 关键词改变
    const handleChangeKeyword = async (ev: any) => {
      const keyword = ev.target?.value;
      searchStore.setKeywords(keyword);
      shopId && searchStore.setShopKeywords(keyword);
      const isEmptyKeyword = !keyword?.trim();
      isEmptyKeyword && handleEmptyKeyword(searchStore);
      if (!isEmptyKeyword) {
        // 搜索防抖
        debouncedHandleSuggestWordsRequest(keyword);
      }
    };

    // 防抖处理
    const handleSuggestWordsRequest = useCallback(
      async (keyword: any) => {
        searchStore.setIsLoading(true);
        const isEmptyKeyword = !keyword?.trim();
        isEmptyKeyword && handleEmptyKeyword(searchStore);
        searchBusinessLogger({
          eventName: SEARCH_EVENT_NAMES.SEARCH_SUG_FETCH_START,
          message: {
            keyword,
          },
        });
        const suggestWordsResult = await getSuggestWords({
          keywords: keyword,
          ...(channelPageName
            ? {
                channelPage: {
                  channelPageName: channelPageName,
                  poolIdList: poolIdList ? [poolIdList] : [],
                },
              }
            : {}),
        });
        if (suggestWordsResult.success && suggestWordsResult.data) {
          searchStore.setSuggestWords(suggestWordsResult.data.suggestItems || []);
        }
        if (suggestWordsResult.data?.suggestItems?.length === 0) {
          searchBusinessLogger({
            eventName: SEARCH_EVENT_NAMES.SEARCH_SUG_N0_DATA,
            message: {
              keyword,
              suggestWordsResult,
            },
          });
        }
        searchStore.setIsLoading(false);
      },
      [searchStore],
    );

    // 对请求逻辑进行防抖处理
    const debouncedHandleSuggestWordsRequest = useRef(
      debounce(handleSuggestWordsRequest, 300),
    ).current;

    // 输入框聚焦
    const handleInputFocus = async (ev: any) => {
      lockPage();
      // 只有在不禁用自动显示下拉框时才设置为 true
      if (!disableAutoShowDropdown) {
        searchStore.setSearchDropdownVisible(true);
      }

      const keyword = ev.target?.value;
      if (keyword) {
        await handleChangeKeyword(ev);
      }
    };

    // 返回上一步
    const handleBack = () => {
      if (searchStore.searchDropdownVisible && searchStore.keywords) {
        searchStore.setSearchDropdownVisible(false);
        searchStore.setKeywords(new URLSearchParams(window.location.search).get('k') || '');
      } else {
        router.back();
      }
    };

    const inputExpoParams = k
      ? baseUrl === '/s-for-addon'
        ? {
            exptag:
              'exp|' +
              (isAddon
                ? EXPOSURE_RECORD32535.FreeShippingAddOn_SearchBox_Expo
                : EXPOSURE_RECORD25103.Promotion_SearchBox_Expo),
            'data-exptag-config':
              '{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}',
            'data-exptag-json-param': JSON.stringify({
              channelName: 'Search_result',
              shopid: shopId || '-100',
            }),
          }
        : {
            'data-exptag-config':
              '{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}',
            'data-exp': JSON.stringify({
              spm: 'a0146.b002226.c00002382.SearchBox',
              biz_type: '',
              event_id: 'Search_ProductList_SearchBox_Expo',
              page_id: 'Search_ProductList',
              json_param: JSON.stringify({
                channelName: 'Search_result',
                shopid: shopId || '-100',
              }),
            }),
          }
      : baseUrl === '/s-for-addon'
        ? {
            exptag:
              'exp|' +
              (isAddon
                ? EXPOSURE_RECORD32535.FreeShippingAddOn_SearchBox_Expo
                : EXPOSURE_RECORD25103.Promotion_SearchBox_Expo),
            'data-exptag-config':
              '{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}',
            'data-exptag-json-param': JSON.stringify({
              channelName: 'Search_result',
              shopid: shopId || '-100',
            }),
          }
        : {
            'data-exptag-config':
              '{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}',
            'data-exp': JSON.stringify({
              spm: 'a0146.b002227.c00002381.SearchBox',
              biz_type: '',
              event_id: 'Search_SearchBox_Expo',
              page_id: 'Search_Activity',
              json_param: JSON.stringify({
                channelName: 'Search_result',
                shopid: shopId || '-100',
              }),
            }),
          };

    const Search_SearchBtn_Expo = k
      ? {
          'data-exptag-config':
            '{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}',
          'data-exp': JSON.stringify({
            spm: 'a0146.b002226.c00002382.Searchbutton',
            biz_type: '',
            event_id: 'Search_ProductList_Search_Expo',
            json_param: JSON.stringify({
              channelName: 'Search_result',
              shopid: shopId || '-100',
            }),
          }),
        }
      : {
          'data-exp': JSON.stringify({
            spm: 'a0146.b002227.c00002381.SearchBtn',
            biz_type: '',
            event_id: 'Search_SearchBtn_Expo',
            json_param: JSON.stringify({
              channelName: 'Search_result',
              shopid: shopId || '-100',
            }), // 原有的data-exptag-json-param
          }),
          'data-exptag-config':
            '{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}',
        };

    return (
      <div id={id} className={`${styles.searchBar} ${containerClass} s_searchBar_container`}>
        <ArrowLeftIcon onClick={searchGoBack || handleBack} />
        <input
          ref={inputRef}
          placeholder={
            shopInfoVO?.shopName && getShopId()
              ? (t('s.searchBar.searchInShop', { shopName: shopInfoVO.shopName }) ??
                t('s.searchBar.placeholder'))
              : typeof defaultWords === 'string' && defaultWords !== 'undefined'
                ? defaultWords
                : t('s.searchBar.placeholder')
          }
          value={searchStore.keywords || searchStore.shopKeywords}
          onChange={handleChangeKeyword}
          onFocus={(e) => {
            handleInputFocus(e);
            setIsInputFocused(true);
          }}
          onKeyDown={(e) => {
            // 点击回车/换行，直接搜索
            if (e.key === 'Enter') {
              handleSearch(e);
            }
          }}
          onClick={onInputClick && onInputClick}
          {...inputExpoParams}
          // //中间页搜索框曝光
          // data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0}'
          // data-exp={JSON.stringify({
          //   spm: 'a0146.b002227.c00002381.SearchBox',
          //   biz_type: '',
          //   event_id: 'Search_SearchBox_Expo',
          //   json_param: JSON.stringify({
          //     channelName: 'Search_result',
          //     shopid: shopId || '-100',
          //     keyWord: searchStore.keywords.trim()
          //       ? searchStore.keywords.trim()
          //       : typeof defaultWords === 'string' && defaultWords && defaultWords !== 'undefined'
          //         ? defaultWords
          //         : '',
          //     searchType: searchStore.keywords.trim()
          //       ? 'active search'
          //       : typeof defaultWords === 'string' && defaultWords && defaultWords !== 'undefined'
          //         ? 'hints'
          //         : '-100',
          //   }), // 原有的data-exptag-json-param
          // })}
        ></input>
        {searchStore.keywords && isInputFocused ? (
          <ClearBtn className={styles.clearBtn} onClick={handleClear} />
        ) : null}
        <SearchBtn
          className={styles.searchBarBtn}
          onClick={handleSearch}
          {...Search_SearchBtn_Expo}
        />
        {searchStore.isLoading && baseUrl === '/s' && (
          <div className={styles.dropdownWrapper}></div>
        )}
        {searchStore.searchDropdownVisible ? (
          <div className={styles.dropdownWrapper}>
            {
              // 联想词
              !rankingSearch &&
              ((searchStore.formatSuggestWords.length > 0 && !searchStore.storeId) ||
                (!!searchStore.keywords && searchStore.formatSuggestWords.length == 0)) ? (
                <ul>
                  {searchStore.formatSuggestWords.map((suggestWord, pos: number) => {
                    const keyword = suggestWord.keyword;
                    const searchTerm = searchStore.keywords.toLowerCase();
                    const lowerKeyword = keyword.toLowerCase();
                    const startIndex = lowerKeyword.indexOf(searchTerm);

                    if (startIndex !== -1 && searchTerm) {
                      const beforeMatch = keyword.substring(0, startIndex);
                      const match = keyword.substring(startIndex, startIndex + searchTerm.length);
                      const afterMatch = keyword.substring(startIndex + searchTerm.length);

                      return (
                        <li
                          className={styles.suggestWordItem}
                          key={suggestWord.keyword}
                          onClick={(e) => {
                            handleSelectKeywords(
                              suggestWord.keyword,
                              suggestWord.miniKeyword,
                              suggestWord.type,
                              suggestWord.suggestShopVO?.shopId,
                              'suggestion',
                              undefined,
                              '1',
                            );
                            spmTrackingUtils.spmClick({
                              spm: 'a0146.b002227.c00002381.SearchBtn',
                              biz_type: '',
                              event_id: 'Search_SearchBtn',
                              page_id: 'Search_Activity',
                              json_param: JSON.stringify({
                                abver: '-100',
                                pvid: logid || '-100',
                                channelName: 'Associative_words',
                                keyWord: searchStore.keywords.trim(),
                                shopid: shopId || '-100',
                                searchtype: 'suggestion',
                                ...searchEventTrack,
                              }),
                              target: e.target as HTMLElement,
                            });
                            // 点击事件上报 0:平台  1:店铺
                            if (suggestWord.type === 1) {
                              spmTrackingUtils.spmClick({
                                spm: 'a0146.b002264.c00002621.Shop_Entry_M',
                                biz_type: '',
                                event_id: 'Search_Sug_Shop_Entry',
                                page_id: 'Search_Activity',
                                json_param: JSON.stringify({
                                  shopid: suggestWord.suggestShopVO?.shopId ?? '-100',
                                  pvid: logid,
                                  keyword: searchStore.keywords,
                                  index: pos,
                                  channelName: 'Associative_words',
                                  ...searchEventTrack,
                                }),
                                target: e.target as HTMLElement,
                              });
                            } else {
                              spmTrackingUtils.spmClick({
                                spm: 'a0146.b002264.c00002620.AssociativeWord_M',
                                biz_type: '',
                                event_id: 'Search_Sug_AssociativeWord',
                                page_id: 'Search_Activity',
                                json_param: JSON.stringify({
                                  abver: '-100',
                                  pvid: logid,
                                  channelName: 'Associative_words',
                                  pos: pos,
                                  keyWord: searchStore.keywords,
                                  Input_Keyword: suggestWord.keyword,
                                  shopid: suggestWord.suggestShopVO?.shopId ?? '-100',
                                  ...searchEventTrack,
                                }),
                                target: e.target as HTMLElement,
                              });
                            }
                          }}
                          data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
                          data-exp={JSON.stringify({
                            spm: 'a0146.b002264.c00002620.AssociativeWord_M',
                            biz_type: '',
                            event_id: 'Search_Sug_AssociativeWord_Expo',
                            json_param: JSON.stringify({
                              abver: '-100',
                              pvid: logid,
                              channelName: 'Associative_words',
                              pos: pos,
                              keyWord: searchStore.keywords,
                              Input_Keyword: suggestWord.keyword,
                              shopid: suggestWord.suggestShopVO?.shopId ?? '-100',
                              ...searchEventTrack,
                            }),
                          })}
                        >
                          {/* type === 2 展示店铺埋点信息 小首页搜索，第一条显示小首页 keyword + in 小首页名称 */}
                          {suggestWord.type === 2 ? (
                            <>
                              {' '}
                              <span className={styles.miniKeyword}>{suggestWord.keyword}</span>
                              <span className={styles.miniTab}>
                                {' '}
                                {suggestWord.miniKeyword}{' '}
                              </span>{' '}
                              <div className={styles.goRight}>
                                <SearchRight />
                              </div>
                            </>
                          ) : (
                            <>
                              {beforeMatch && (
                                <span className={styles.beforeMatch}>{beforeMatch}</span>
                              )}
                              {/* 加粗部分 */}
                              <div className={styles.matchContainer}>
                                {/* 左侧keyword不高亮 */}
                                {match}
                                {/* 右侧联想部分高亮加粗 */}
                                {afterMatch && (
                                  <span className={styles.afterMatch}>{afterMatch}</span>
                                )}
                              </div>
                              {/*  店铺和默认搜索样式 */}
                              {/* type === 1 展示店铺埋点信息 */}
                              {suggestWord.type === 1 ? (
                                <div className={styles.goRight}>
                                  <span
                                    className={styles.shopIcon}
                                    data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.5}'
                                    data-exp={JSON.stringify({
                                      spm: 'a0146.b002264.c00002621.Shop_Entry_M',
                                      biz_type: '',
                                      event_id: 'Search_Sug_Shop_Entry_Expo',
                                      json_param: JSON.stringify({
                                        shopid: suggestWord.suggestShopVO?.shopId ?? '-100',
                                        keyword: searchStore.keywords,
                                        channelName: 'Associative_words',
                                        pvid: logid,
                                        index: pos,
                                        ...searchEventTrack,
                                      }),
                                    })}
                                  >
                                    <CDNImage
                                      src={suggestWord.suggestShopVO?.shopLogoUrl || ''}
                                      width={24}
                                      height={24}
                                    />
                                  </span>
                                  <ShopRight />
                                </div>
                              ) : (
                                <div className={styles.goRight}>
                                  <SearchRight />
                                </div>
                              )}
                            </>
                          )}
                        </li>
                      );
                    }
                    // 如果没有匹配到搜索词，整个词不加粗
                    return (
                      <li
                        className={styles.suggestWordItem}
                        key={suggestWord.keyword}
                        onClick={(e) => {
                          searchStore.setIsLoading(true);
                          // @ts-ignore:next-line
                          handleSelectKeywords(suggestWord.keyword);
                          spmTrackingUtils.spmClick({
                            spm: 'a0146.b002264.c00002620.AssociativeWord_M',
                            biz_type: '',
                            event_id: 'Search_Sug_AssociativeWord',
                            page_id: 'Search_Activity',
                            json_param: JSON.stringify({
                              abver: '-100',
                              pvid: logid,
                              channelName: 'Associative_words',
                              pos: pos,
                              keyWord: searchStore.keywords,
                              Input_Keyword: suggestWord.keyword,
                              shopid: suggestWord.suggestShopVO?.shopId ?? '-100',
                              ...searchEventTrack,
                            }),
                            target: e.target as HTMLElement,
                          });
                          spmTrackingUtils.spmClick({
                            spm: 'a0146.b002227.c00002381.SearchBtn',
                            biz_type: '',
                            event_id: 'Search_SearchBtn',
                            page_id: 'Search_Activity',
                            json_param: JSON.stringify({
                              abver: '-100',
                              pvid: logid || '-100',
                              channelName: 'Associative_words',
                              keyWord: searchStore.keywords,
                              shopid: shopId || '-100',
                              searchtype: 'suggestion',
                              ...searchEventTrack,
                            }),
                            target: e.target as HTMLElement,
                          });
                        }}
                        data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
                        data-exp={JSON.stringify({
                          spm: 'a0146.b002264.c00002620.AssociativeWord_M',
                          biz_type: '',
                          event_id: 'Search_Sug_AssociativeWord_Expo',
                          json_param: JSON.stringify({
                            abver: '-100',
                            pvid: logid,
                            channelName: 'Associative_words',
                            pos: pos,
                            keyWord: searchStore.keywords,
                            Input_Keyword: suggestWord.keyword,
                            shopid: suggestWord.suggestShopVO?.shopId ?? '-100',
                            ...searchEventTrack,
                          }),
                        })}
                      >
                        {keyword}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                // 热词和历史词
                <>
                  {searchStore.recentKeywords.length > 0 && !searchStore.storeId ? (
                    <>
                      {/*最近搜索*/}
                      <div
                        className={styles.titleWrapper}
                        data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
                        data-exp={JSON.stringify({
                          spm: 'a0146.b002227.c00002379.History',
                          biz_type: '',
                          event_id: 'Search_History_Expo',
                          page_id: 'Search_Activity',
                          json_param: JSON.stringify({
                            abver: '-100',
                            pvid: logid,
                            logid: searchEventTrack?.logid || '-100',
                            channelName: 'Search_result',
                            keyWord: searchStore.recentKeywords.join(','),
                            shopid: shopId || '-100',
                          }),
                        })}
                      >
                        <span>{t('s.searchBar.recentlySearched')}</span>
                        <DeleteIcon
                          data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
                          data-exp={JSON.stringify({
                            spm: 'a0146.b002227.c00002379.delet',
                            biz_type: '',
                            event_id: 'Search_Delete_Expo',
                            page_id: 'Search_Activity',
                            json_param: JSON.stringify({
                              channelName: 'Search_result',
                              shopid: shopId || '-100',
                            }),
                          })}
                          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                            searchStore.setIsLoading(true);
                            handleClearRecentKeywords();
                            spmTrackingUtils.spmClick({
                              spm: 'a0146.b002227.c00002379.delet',
                              biz_type: '',
                              event_id: 'Search_Delete',
                              page_id: 'Search_Activity',
                              json_param: JSON.stringify({
                                channelName: 'Search_result',
                              }),
                              target: e.target as HTMLElement,
                            });
                          }}
                        ></DeleteIcon>
                      </div>
                      <div className={styles.tags}>
                        {searchStore.recentKeywords.map((keyword: string, index: number) => {
                          return (
                            <span
                              key={index}
                              className={styles.tag}
                              onClick={(e) => {
                                searchStore.setIsLoading(true);
                                // @ts-ignore:next-line
                                handleSelectKeywords(
                                  keyword,
                                  '', // miniKeyword 为空
                                  undefined, // type 为空
                                  undefined, // storeId 为空
                                  'search history', // 历史搜索
                                  undefined,
                                  '3', // 搜索词类型为-历史搜索
                                );
                                spmTrackingUtils.spmClick({
                                  spm: 'a0146.b002227.c00002379.History',
                                  biz_type: '',
                                  event_id: 'Search_History',
                                  page_id: 'Search_Activity',
                                  json_param: JSON.stringify({
                                    abver: '-100',
                                    pvid: logid,
                                    channelName: 'Search_result',
                                    keyWord: keyword,
                                    shopid: shopId || '-100',
                                    ...searchEventTrack,
                                  }),
                                  target: e.target as HTMLElement,
                                });
                                spmTrackingUtils.spmClick({
                                  spm: 'a0146.b002227.c00002381.SearchBtn',
                                  biz_type: '',
                                  event_id: 'Search_SearchBtn',
                                  page_id: 'Search_Activity',
                                  json_param: JSON.stringify({
                                    abver: '-100',
                                    pvid: logid || '-100',
                                    channelName: 'Search_result',
                                    keyWord: keyword,
                                    shopid: shopId || '-100',
                                    searchtype: 'search history',
                                    ...searchEventTrack,
                                  }),
                                  target: e.target as HTMLElement,
                                });
                              }}
                            >
                              {keyword}
                            </span>
                          );
                        })}
                      </div>
                    </>
                  ) : null}
                  {/*热词搜索*/}
                  {searchStore.hotWords.length ? (
                    <>
                      <div className={styles.titleWrapper}>
                        <span>
                          {getShopId() ? t('s.searchBar.trends') : t('s.searchBar.hotSearch')}
                        </span>
                      </div>

                      <div className={styles.tags}>
                        {searchStore.hotWords.map((item: any, index: number) => {
                          return (
                            <span
                              key={item.keyword}
                              className={styles.tag}
                              onClick={(e) => {
                                searchStore.setIsLoading(true);
                                // @ts-ignore:next-line
                                handleSelectKeywords(
                                  item.keyword,
                                  '', // miniKeyword 为空
                                  undefined, // type 为空
                                  undefined, // storeId 为空
                                  'hot search', // 热词搜索
                                  item.extMap,
                                  '2', // 搜索词类型-热词
                                );
                                spmTrackingUtils.spmClick({
                                  spm: 'a0146.b002227.c00002380.Hotword',
                                  biz_type: '',
                                  event_id: 'Search_Hotword',
                                  page_id: 'Search_Activity',
                                  json_param: JSON.stringify({
                                    abver: '-100',
                                    pvid: logid,
                                    channelName: 'Search_result',
                                    pos: index,
                                    keyWord: item.keyword,
                                    shopid: shopId || '-100',
                                    ...searchEventTrack,
                                  }),
                                  target: e.target as HTMLElement,
                                });
                                spmTrackingUtils.spmClick({
                                  spm: 'a0146.b002227.c00002381.SearchBtn',
                                  biz_type: '',
                                  event_id: 'Search_SearchBtn',
                                  page_id: 'Search_Activity',
                                  json_param: JSON.stringify({
                                    abver: '-100',
                                    pvid: logid || '-100',
                                    channelName: 'Search_result',
                                    keyWord: item.keyword,
                                    shopid: shopId || '-100',
                                    searchtype: 'hot search',
                                    ...searchEventTrack,
                                  }),
                                  target: e.target as HTMLElement,
                                });
                              }}
                              //中间页热词曝光
                              data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                              data-exp={JSON.stringify({
                                spm: 'a0146.b002227.c00002380.Hotword',
                                biz_type: '',
                                event_id: 'Search_Hotword_Expo',
                                page_id: 'Search_Activity',
                                json_param: JSON.stringify({
                                  abver: '-100',
                                  pvid: logid,
                                  channelName: 'Search_result',
                                  pos: index,
                                  keyWord: item.keyword,
                                  shopid: shopId || '-100',
                                  request_id: item?.extMap?.requestId,
                                  algo_rd_inf: item?.extMap?.algoid,
                                  ...searchEventTrack,
                                }),
                              })}
                            >
                              {item.keyword}
                            </span>
                          );
                        })}
                      </div>
                    </>
                  ) : null}
                </>
              )
            }
          </div>
        ) : null}

        {/* 删除二次确认弹窗 */}
        <UKDialog
          open={againDelete}
          cancelButtonText={t('common.btnText.cancel')}
          handleCancel={() => {
            setAgainDelete(false);
          }}
          handleClose={deleteRecentKeywords}
        >
          <div className={styles.del_dialog_text}>{t('s.common.sureDelete')}</div>
        </UKDialog>
      </div>
    );
  },
);

export default SearchBar;
